import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { UserProfile, UserRole } from "../types";
import { useAuth } from "../context/AuthContext";

// Firestore: used only to fetch the global default logo URL (not hardcoded here)
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

interface LayoutProps {
  children: ReactNode;
  user: UserProfile;
  currentView: string;
  onNavigate: (view: string) => void;
  onLogout: () => void;
}

type BrandingConfig = {
  defaultLogoUrl?: string;
};

const BRANDING_DOC_PATH = { collection: "appConfig", doc: "branding" };

const Layout: React.FC<LayoutProps> = ({ children, user, currentView, onNavigate }) => {
  const { firmSettings, logout } = useAuth();

  const isStaff = user.role !== UserRole.CLIENT;
  const isDevSession = user.uid?.startsWith("dev_");

  const brandColor = firmSettings?.brandColor || "#27b3e6";

  const [branding, setBranding] = useState<BrandingConfig>({});
  const [brandingLoaded, setBrandingLoaded] = useState(false);

  // Fetch global branding config once (default logo URL lives here)
  useEffect(() => {
    let mounted = true;

    const loadBranding = async () => {
      try {
        const snap = await getDoc(doc(db, BRANDING_DOC_PATH.collection, BRANDING_DOC_PATH.doc));
        if (!mounted) return;

        if (snap.exists()) {
          const data = snap.data() as BrandingConfig;
          setBranding({ defaultLogoUrl: data?.defaultLogoUrl || "" });
        } else {
          setBranding({ defaultLogoUrl: "" });
        }
      } catch (e) {
        // If branding fetch fails, we still want the app to work.
        setBranding({ defaultLogoUrl: "" });
      } finally {
        if (mounted) setBrandingLoaded(true);
      }
    };

    loadBranding();

    return () => {
      mounted = false;
    };
  }, []);

  const logoSrc = useMemo(() => {
    const firmLogo = (firmSettings?.logoUrl || "").trim();
    if (firmLogo) return firmLogo;

    const fallbackLogo = (branding?.defaultLogoUrl || "").trim();
    if (fallbackLogo) return fallbackLogo;

    return "";
  }, [firmSettings?.logoUrl, branding?.defaultLogoUrl]);

  const firmName = (firmSettings?.name || "Filers Hub").trim();

  const NavItem = ({ view, icon, label }: { view: string; icon: string; label: string }) => {
    const isActive = currentView === view;
    return (
      <button
        onClick={() => onNavigate(view)}
        style={isActive ? { backgroundColor: brandColor, color: "white" } : {}}
        className={`flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-tighter transition-all duration-200 rounded-xl whitespace-nowrap ${
          isActive ? "shadow-lg scale-105" : "text-slate-500 hover:bg-slate-50 hover:text-black"
        }`}
      >
        <span className="text-base">{icon}</span>
        <span className="hidden lg:inline">{label}</span>
      </button>
    );
  };

  return (
    <div className="flex flex-col min-h-screen w-full bg-slate-50 overflow-x-hidden">
      <header className="sticky top-0 z-50 shadow-sm bg-white text-black border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 h-20 grid grid-cols-3 items-center">
          {/* Left Column */}
          <div className="flex items-center justify-start gap-4">
            <button
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all border border-transparent hover:border-red-100"
            >
              <span className="text-base">🚪</span>
              <span className="hidden sm:inline">Sign Out</span>
            </button>

            {isDevSession && (
              <div className="px-3 py-1 bg-amber-100 text-amber-700 text-[9px] font-black rounded-lg border border-amber-200 uppercase tracking-tighter animate-pulse">
                Dev Mode
              </div>
            )}
          </div>

          {/* Center Column */}
          <nav className="hidden md:flex items-center justify-center gap-1">
            <NavItem view="dashboard" icon="📊" label="Dashboard" />
            {isStaff && <NavItem view="clients" icon="👥" label="Clients" />}
            <NavItem view="documents" icon="📁" label="Documents" />
            {(user.role === UserRole.FirmOwner || user.role === UserRole.Manager) && (
              <NavItem view="settings" icon="⚙️" label="Firm" />
            )}
          </nav>

          {/* Right Column: Logo */}
          <div className="flex items-center justify-end">
            <div className="h-12 w-[160px] flex items-center justify-center p-2 rounded-xl bg-white select-none">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt={`${firmName} Logo`}
                  className="max-h-full max-w-full object-contain pointer-events-none"
                  onError={(e) => {
                    // If firm logo fails, try fallback default (from branding doc).
                    const fallback = (branding?.defaultLogoUrl || "").trim();
                    if (fallback && e.currentTarget.src !== fallback) {
                      e.currentTarget.src = fallback;
                      return;
                    }
                    // If fallback also fails, hide the broken image.
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                  {brandingLoaded ? "Filers Hub" : "Loading..."}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-7xl mx-auto p-4 md:p-8">
        <div className="relative animate-fade-in-up">{children}</div>
      </main>

      <footer className="py-12 text-center text-[10px] font-black uppercase tracking-[0.3em] text-slate-300">
        Secure Portal Gateway • {firmName} • &copy; {new Date().getFullYear()}
      </footer>
    </div>
  );
};

export default Layout;
